import React, { useContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
// ui
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Box from "@mui/material/Box";
// components
import useLocaleRoute from "../../../../../../common/hooks/useLocaleRoute";
import useModulePublic from "../../../../../hooks/useModulePublic";
import IconPopup from "../../../../../../common/components/popup/IconPopup";
import RadioSelector from "../../common/RadioSelector";
import PopupListItem from "../../common/PopupListItem";
import CommonList from "../../common/CommonList";
import CommonListItem from "../../common/CommonListItem";
import { StoreContext } from "../../../../../../store/StoreProvider";
import useModuleLimit from "../../../../../hooks/useModuleLimit";
// config, utils
import urls from "../../../../../../api/project/ApiUrls";
import { MODULE_ID, MODULE_LIMIT_ID } from "../../../../../../config/CONSTANTS";
import { selectOpts } from "./option";
import Act from "../../../../../../store/actions";
import role from "../../../../../../config/App-role";
import fireAuth from "../../../../../../api/firebase/auth/utils";
import fireDb from "../../../../../../api/firebase/realtime/utils";
import {
  deleteGroupUser,
  getUsersInfo,
} from "../../../../service/userGroupService";

const popupMenuPosition = {
  anchorOrigin: {
    vertical: "bottom",
    horizontal: "left",
  },
  transformOrigin: {
    vertical: "top",
    horizontal: "left",
  },
};

const StyledList = CommonList;

const AccordionDetailChildren = () => {
  // props

  // route,intl
  const intl = useIntl();
  const navigate = useNavigate();
  // store
  const [state, dispatch] = useContext(StoreContext);
  const { user } = state;
  const { locale, group } = user;
  // local state
  const [listItems, setListItems] = useState([]);
  const [listItemFocus, setListItemFocus] = useState(null);
  const [popupPageIdx, setPopupPageIdx] = useState(0);
  const [popPageCtx, setPopPageCtx] = useState({});
  // hooks
  const { handleLocaleRoute } = useLocaleRoute(
    locale,
    urls?.locale_lang?.LOCALE_DEFAULT
  );
  const { handlePublic } = useModulePublic();
  const { handleLimit } = useModuleLimit();

  const disabledListItemMoreAct = !(
    handlePublic(MODULE_ID.snaSidebarGroupEditmember) &&
    fireAuth.isGroupAdmin(group)
  );

  // 使用者自己不用顯示在 團隊成員名單中
  const filterOutSelf = (item) => item?.uid !== user?.uid;

  const fetchGroupUsers = (groupMembers) => {
    const membersId = Object.entries(groupMembers || group.members)
      .filter(([memberId, valObj]) => valObj.isMember)
      .map(([memberId, isMember]) => memberId);
    // 使用者自己不用顯示在 團隊成員名單中
    const getUsersInfoSuccess = (usersFind) => {
      // console.log("usersFind", usersFind);
      setListItems(usersFind);
    };
    const getUsersInfoError = (err) => {
      dispatch({
        type: Act.COMMON_SET_ERROR_MESSAGE,
        payload: {
          open: true,
          content: err.message,
        },
      });
    };
    getUsersInfo(membersId, group?.uid, getUsersInfoSuccess, getUsersInfoError);
  };

  // current groupId 變動時, 重新取得所有成員的資料
  useEffect(() => {
    if (group?.uid && group?.members) {
      fetchGroupUsers();
    }
  }, [group]);

  const handleRoleChange = (userInfo) => (val) => {
    // 儲存至 firebase, 儲存成功的話重新抓取 group users 資料
    fireDb.updateGroupMember({
      userId: userInfo?.uid,
      groupId: group?.uid,
      data: { isMember: true, role: val, uid: userInfo?.uid },
      onSuccess: () => {
        dispatch({
          type: Act.COMMON_SET_SUCCESS_MESSAGE,
          payload: {
            open: true,
            content: intl.formatMessage({
              id: "sna.sidebar.grouptool.edit.member.role.success",
              defaultMessage: "更改權限成功",
            }),
          },
        });
      },
      onError: (err) => {
        dispatch({
          type: Act.COMMON_SET_ERROR_MESSAGE,
          payload: {
            open: true,
            content: err.message,
          },
        });
      },
    });
    fetchGroupUsers();
  };

  const handleDeleteListItem = (itemInfo) => () => {
    // 儲存至 firebase, 儲存成功的話重新抓取 user groups 資料
    const successDeleteCallback = () => {
      // 儲存至 firebase, 儲存成功的話重新抓取 group users 資料
      dispatch({
        type: Act.COMMON_SET_SUCCESS_MESSAGE,
        payload: {
          open: true,
          content: intl.formatMessage({
            id: "sna.sidebar.grouptool.delete.member.success",
            defaultMessage: "刪除成員成功",
          }),
        },
      });
      const newMembers = Object.keys(group.members)
        .filter((uid) => uid !== itemInfo?.uid)
        .map((uid) => group.members[uid]);
      fetchGroupUsers(newMembers);
    };

    const failureDeleteCallback = (err) => {
      // 彈跳視窗 => 失敗訊息
      dispatch({
        type: Act.COMMON_SET_ERROR_MESSAGE,
        payload: {
          open: true,
          content: err.message,
        },
      });
    };

    dispatch({
      type: Act.SNA_DIALOG_CONTEXT,
      payload: {
        openSignal: true,
        title: intl.formatMessage({
          id: "sna.sidebar.grouptool.delete.groupmember.modal.title",
          defaultMessage: "確定要刪除成員嗎？",
        }),
        contentText: intl.formatMessage({
          id: "sna.sidebar.grouptool.delete.groupmember.modal.text",
          defaultMessage: "刪除後該成員將無法使用團隊內容。",
        }),
        onYes: () =>
          deleteGroupUser({
            userId: itemInfo?.uid,
            groupId: group?.uid,
            onSuccess: successDeleteCallback,
            onFailure: failureDeleteCallback,
          }),
        yesText: intl.formatMessage({
          id: "sna.sidebar.grouptool.delete.groupmember.modal.confirm",
          defaultMessage: "刪除",
        }),
        noText: intl.formatMessage({
          id: "sna.sidebar.grouptool.delete.groupmember.modal.cancel",
          defaultMessage: "取消",
        }),
      },
    });
  };

  const getPopupPageChangeRole = () =>
    !disabledListItemMoreAct && {
      children: (
        <PopupListItem
          text={intl.formatMessage({
            id: "sna.sidebar.grouptool.edit.member.role",
            defaultMessage: "更改權限",
          })}
          onClick={() => setPopupPageIdx(1)}
          withNextStep
        />
      ),
    };

  const getPopupPageDelete = () =>
    !disabledListItemMoreAct && {
      children: (
        <PopupListItem
          text={intl.formatMessage({
            id: "sna.sidebar.grouptool.delete.member",
            defaultMessage: "刪除成員",
          })}
          onClick={handleDeleteListItem(listItemFocus)}
          withNextStep={false}
        />
      ),
    };

  const popupPage0 = {
    body: [getPopupPageChangeRole(), getPopupPageDelete()].filter(
      (item) => !!item
    ),
  };

  const disabledSelectOpt = (opt) => {
    const adminUsersCount = listItems.filter(
      (item) => item.role === role.admin
    ).length;
    if (!listItemFocus) return false;
    // 使用者若是 admin, 則不可變更權限
    if (listItemFocus?.uid === user?.uid) {
      return true;
    }
    if (opt.value === role.admin) {
      // admin 的數量有限制
      return (
        adminUsersCount >=
        handleLimit(MODULE_LIMIT_ID.ADMINS_OF_ONE_GROUP_MAX_LIMIT)
      );
    }
    // editor, reader 的成員數量沒有限制
    return false;
  };

  const popupPage1 = {
    body: (
      <Box px={2} py={1}>
        <RadioSelector
          label={intl.formatMessage({
            id: "sna.sidebar.grouptool.choose.role",
            defaultMessage: "選擇角色",
          })}
          option={selectOpts.map((opt) => ({
            value: opt.value,
            label: opt.label,
            disabled: disabledSelectOpt(opt),
          }))}
          value={listItemFocus?.role || ""}
          onChange={handleRoleChange(listItemFocus)}
        />
      </Box>
    ),
  };

  const getPopupPage0 = () => {
    if (fireAuth.isGroupAdmin(group)) {
      return popupPage0;
    }
    return null;
  };

  const getPopupPage1 = () => {
    if (fireAuth.isGroupAdmin(group)) {
      return popupPage1;
    }
    return null;
  };

  // popup 所有分頁
  const popupPages = [getPopupPage0(), getPopupPage1()].filter(
    (item) => !!item
  );

  // 當選擇的 user 變更 or pageIdx 便更時, 重新產生 popup 內容
  useEffect(() => {
    setPopPageCtx(popupPages[popupPageIdx]);
  }, [popupPageIdx, listItemFocus]);

  const getIconButtonProps = (gUser) => ({
    onButtonIconClick: () => {
      setListItemFocus(gUser);
    },
    sx: {
      color: "#fff",
    },
    disabled: disabledListItemMoreAct,
  });

  const getMenuProps = () => ({
    ...popPageCtx,
    ...popupMenuPosition,
    onClose: () => setPopupPageIdx(0),
  });

  return (
    <StyledList>
      {/* // 使用者自己不用顯示在 團隊成員名單中 */}
      {listItems.filter(filterOutSelf).map((gUser, idx) => (
        <CommonListItem
          key={idx.toString()}
          context={{
            id: gUser?.uid,
            textPrimary: gUser.displayName,
          }}
          avatarSize="normal"
          secondaryAction={
            <IconPopup
              icon={<MoreHorizIcon />}
              iconButtonProps={{
                ...getIconButtonProps(gUser),
              }}
              menuProps={{
                ...getMenuProps(),
              }}
            />
          }
          darkMode
        />
      ))}
    </StyledList>
  );
};

export default AccordionDetailChildren;
